import React from "react";
import { useQuery, useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";

import { LoaderDots } from "../../../../middleware/loaders";
import {
  GET_MY_SUBSCRIPTION,
  GET_SUBSCRIPTION_HISTORY,
  CANCEL_SUBSCRIPTION,
  REACTIVATE_SUBSCRIPTION,
  MANAGE_BILLING,
  GetMySubscriptionResponse,
  GetSubscriptionHistoryResponse,
  CancelSubscriptionResponse,
  ReactivateSubscriptionResponse,
  ManageBillingResponse,
} from "../../subscription/queries";

const statusLabels: Record<string, { text: string; color: string }> = {
  active: { text: "Activa", color: "#48BB78" },
  past_due: { text: "Pago pendiente", color: "#FF6028" },
  cancelled: { text: "Cancelada", color: "#999" },
  payed: { text: "Pagada", color: "#48BB78" },
  expired: { text: "Expirada", color: "#999" },
  pending: { text: "Pendiente", color: "#FFBB28" },
  sent: { text: "Enviada", color: "#17a2b8" },
};

const planLabels: Record<string, string> = {
  basic: "Basico",
  premium: "Premium",
  pro: "Pro",
};

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("es-MX", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const Subscriptions: React.FC = () => {
  const {
    data: subData,
    loading: subLoading,
    refetch: refetchSub,
  } = useQuery<GetMySubscriptionResponse>(GET_MY_SUBSCRIPTION, {
    fetchPolicy: "cache-and-network",
  });

  const { data: historyData, loading: historyLoading } =
    useQuery<GetSubscriptionHistoryResponse>(GET_SUBSCRIPTION_HISTORY, {
      fetchPolicy: "cache-and-network",
    });

  const [cancelSubscription, { loading: cancelLoading }] =
    useMutation<CancelSubscriptionResponse>(CANCEL_SUBSCRIPTION);

  const [reactivateSubscription, { loading: reactivateLoading }] =
    useMutation<ReactivateSubscriptionResponse>(REACTIVATE_SUBSCRIPTION);

  const [manageBilling, { loading: billingLoading }] =
    useMutation<ManageBillingResponse>(MANAGE_BILLING);

  const handleCancel = async () => {
    const confirmed = window.confirm(
      "Tu suscripcion seguira activa hasta el final del periodo de facturacion. ¿Deseas cancelar?",
    );
    if (!confirmed) return;
    try {
      await cancelSubscription();
      toast.success("Suscripcion marcada para cancelar al final del periodo");
      refetchSub();
    } catch (e: any) {
      toast.error(e.message || "Error al cancelar");
    }
  };

  const handleReactivate = async () => {
    try {
      await reactivateSubscription();
      toast.success("Suscripcion reactivada");
      refetchSub();
    } catch (e: any) {
      toast.error(e.message || "Error al reactivar");
    }
  };

  const handleManageBilling = async () => {
    try {
      const { data } = await manageBilling();
      if (data?.manageBilling.url) {
        window.location.href = data.manageBilling.url;
      }
    } catch (e: any) {
      toast.error(e.message || "Error al abrir portal de facturacion");
    }
  };

  if (subLoading || historyLoading) {
    return <LoaderDots />;
  }

  const subscription = subData?.getMySubscription;
  const history = historyData?.getSubscriptionHistory || [];

  return (
    <div className="row justify-content-center mt-3">
      <div className="col-md-10">
        {/* Active Subscription */}
        <div className="card mb-4">
          <div className="card-body">
            <h4 className="card-title mb-3">Suscripcion Activa</h4>
            {subscription ? (
              <>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h3 className="mb-1">
                      {planLabels[subscription.plan_type] ||
                        subscription.plan_type}
                    </h3>
                    <span
                      className="badge"
                      style={{
                        backgroundColor:
                          statusLabels[subscription.status]?.color || "#999",
                        color: "#fff",
                      }}
                    >
                      {statusLabels[subscription.status]?.text ||
                        subscription.status}
                    </span>
                    {subscription.cancel_at_period_end && (
                      <span className="badge bg-warning text-dark ml-2">
                        Se cancela al final del periodo
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    {subscription.amount && (
                      <p className="mb-1">
                        <strong>
                          ${subscription.amount.toLocaleString("es-MX")} /año (incluye IVA)
                        </strong>
                      </p>
                    )}
                    {subscription.period_end && (
                      <small className="text-muted">
                        Vence: {formatDate(subscription.period_end)}
                      </small>
                    )}
                  </div>
                </div>
                <div>
                  {subscription.status === "active" &&
                    !subscription.cancel_at_period_end && (
                      <button
                        className="btn btn-outline-danger btn-sm mr-2"
                        onClick={handleCancel}
                        disabled={cancelLoading}
                      >
                        {cancelLoading ? "Cancelando..." : "Cancelar Suscripcion"}
                      </button>
                    )}
                  {subscription.cancel_at_period_end && (
                    <button
                      className="btn btn-outline-success btn-sm mr-2"
                      onClick={handleReactivate}
                      disabled={reactivateLoading}
                    >
                      {reactivateLoading ? "Reactivando..." : "Reactivar Suscripcion"}
                    </button>
                  )}
                  <button
                    className="btn btn-outline-primary btn-sm mr-2"
                    onClick={handleManageBilling}
                    disabled={billingLoading}
                  >
                    <i className="fas fa-credit-card mr-1" />
                    Metodos de Pago
                  </button>
                  <Link
                    to="/subscription"
                    className="btn btn-warning btn-sm"
                  >
                    <i className="fas fa-arrow-up mr-1" />
                    Cambiar Plan
                  </Link>
                </div>
              </>
            ) : (
              <div className="text-center py-3">
                <p className="text-muted mb-2">
                  No tienes una suscripcion activa.
                </p>
                <Link to="/subscription" className="btn btn-primary">
                  Ver Planes
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Purchase History */}
        <div className="card">
          <div className="card-body">
            <h4 className="card-title mb-3">Historial de Pagos</h4>
            {history.length === 0 ? (
              <p className="text-muted text-center py-3">
                No hay registros de pago.
              </p>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Plan</th>
                      <th>Monto</th>
                      <th>Tipo de Pago</th>
                      <th>Periodo</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((item) => {
                      const st = statusLabels[item.status] || {
                        text: item.status,
                        color: "#999",
                      };
                      return (
                        <tr key={item.id}>
                          <td>{formatDate(item.date_created)}</td>
                          <td>
                            {item.plan_type
                              ? planLabels[item.plan_type] || item.plan_type
                              : "-"}
                          </td>
                          <td>
                            {item.amount
                              ? `$${item.amount.toLocaleString("es-MX")}`
                              : "-"}
                          </td>
                          <td>{item.payment_type || "-"}</td>
                          <td>
                            {item.period_start || item.period_end
                              ? `${formatDate(item.period_start)} - ${formatDate(item.period_end)}`
                              : "-"}
                          </td>
                          <td>
                            <span
                              className="badge"
                              style={{
                                backgroundColor: st.color,
                                color: "#fff",
                              }}
                            >
                              {st.text}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscriptions;
